import React, { useState, useEffect } from 'react';
import { openNotificationWithIcon, PageContent, PermissionGuard, useAssetParams, useAssetQuery, useAssetTranslation, useHasAssetPermissions } from '@mss/mss-component-library';
import { Table, Switch, Input, Select, InputNumber, Button, message, notification } from 'antd';
import { useTranslation } from 'react-i18next';
import Fetch from '../../../util/_fetch';

type TagMapping = {
  enabled: boolean;
  data_field: string;
  data_format: 'int' | 'float' | 'str' | 'bool';
  node_id: string;
  action: 'multiply' | 'divide' | 'none';
  action_value: number;
};

const EDIT_PERMISSION = 'change_fieldassetprm';
export default function FieldPRMOPCConfiguration() {
  const { t } = useAssetTranslation();
  const { t: tCommon } = useTranslation('common');
  const { assetId } = useAssetParams();
  const { data: asset, isLoading } = useAssetQuery(true);
  const permissions = useHasAssetPermissions();
  const [mappings, setMappings] = useState<TagMapping[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (asset?.opc_config?.tag_mappings) {
      setMappings(asset.opc_config.tag_mappings);
    } else {
      setMappings([
        {
          enabled: false,
          data_field: 'UPPER_RANGE_VALUE',
          data_format: 'float',
          node_id: '',
          action: 'none',
          action_value: 1.0
        },
        {
          enabled: false,
          data_field: 'LOWER_RANGE_VALUE',
          data_format: 'float',
          node_id: '',
          action: 'none',
          action_value: 1.0
        }
      ]);
    }
  }, [asset]);

  const successNotification = () => {
    openNotificationWithIcon(
      notification.success,
      t('OPC configuration was updated successfully.'),
      ''
    );
  };

  const failedNotification = () => {
    openNotificationWithIcon(
      notification.error,
      t('OPC configuration could not be updated'),
      ''
    );
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await Fetch.patch(`/api/assets/field/prm/${assetId}/`, {
        body: JSON.stringify({
          opc_config: {
            tag_mappings: mappings
          }
        })
      });
      successNotification();
    } catch (err) {
      failedNotification();
    } finally {
      setSaving(false);
    }
  };

  const updateRow = (index: number, field: keyof TagMapping, value: any) => {
    setMappings((m) =>
      m.map((row, i) => (i === index ? { ...row, [field]: value } : row))
    );
  };

  const columns = [
    {
      title: t('Status'),
      width: 80,
      render: (_: any, record: TagMapping, index: number) => (
        <Switch
          checked={record.enabled}
          onChange={(v) => updateRow(index, 'enabled', v)}
          disabled={!permissions.change}
        />
      )
    },
    {
      title: t('Data field'),
      render: (_: any, record: TagMapping) => {
        if (record.data_field === 'UPPER_RANGE_VALUE') return <span>Upper range</span>;
        if (record.data_field === 'LOWER_RANGE_VALUE') return <span>Lower range</span>;
        return <span>{record.data_field}</span>;
      }
    },
    {
      title: t('Data format'),
      width: 120,
      render: (_: any, record: TagMapping) => {
        const labels: any = {
          int: 'Integer',
          float: 'Float',
          str: 'String',
          bool: 'Boolean',
        };
        return <span>{labels[record.data_format] || record.data_format}</span>;
      }
    },
    {
      title: t('OPC Item ID'),
      render: (_: any, record: TagMapping, index: number) => (
        <Input
          value={record.node_id}
          onChange={(e) => updateRow(index, 'node_id', e.target.value)}
          placeholder="OPC UA Tag Path"
          disabled={!permissions.change}
        />
      )
    },
    {
      title: t('Modifier'),
      width: 120,
      render: (_: any, record: TagMapping, index: number) => (
        <Select
          value={record.action}
          onChange={(v) => updateRow(index, 'action', v)}
          options={[
            { value: 'none', label: t('None') },
            { value: 'multiply', label: 'Multiply' },
            { value: 'divide', label: 'Divide' }
          ]}
          disabled={!permissions.change}
        />
      )
    },
    {
      title: t('Modify value'),
      width: 120,
      render: (_: any, record: TagMapping, index: number) => (
        <InputNumber
          value={record.action_value}
          onChange={(v) => updateRow(index, 'action_value', v ?? 1.0)}
          min={0}
          step={0.1}
          style={{ width: '100%' }}
          disabled={!permissions.change || record.action === 'none'}
        />
      )
    }
  ];

  if (isLoading) {
    return (
      <PageContent title={t('OPC Configuration')}>
        {tCommon('Loading')}...
      </PageContent>
    );
  }

  return (
    <PageContent title={t('OPC Configuration')}>
      <Table
        dataSource={mappings.map((row, i) => ({ ...row, key: i }))}
        columns={columns}
        pagination={false}
        rowKey="key"
        size='small'
      />

      <div style={{ marginTop: 12, display: 'flex', justifyContent: 'flex-end' }}>
      <PermissionGuard codename={[EDIT_PERMISSION]} objectId={assetId}>
          <Button type="primary" loading={saving} onClick={handleSave}>
            {tCommon('Save configuration')}
          </Button>
        </PermissionGuard>
      </div>
    </PageContent>
  );
}
